import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { linkPhoneToUid } from '../../services/userProfileService';
import { isInRnWebView } from '../../bridge/webviewBridge';

const ACCENT = "#4A6741";

const isIosWebView = () => {
  return isInRnWebView() && /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const CompleteProfilePage = () => {
  const navigate = useNavigate();
  const { currentUser, userData, refreshUser, logout } = useAuth();
  const hasPrefilledName = !!(userData?.name || currentUser?.displayName);

  const [form, setForm] = useState({
    name: userData?.name || currentUser?.displayName || '',
    companyName: userData?.companyName || '',
    representative: userData?.representative || '',
    companyAddress: userData?.companyAddress || '',
    companyAddressDetail: userData?.companyAddressDetail || '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddr, setShowAddr] = useState(false);
  const addrRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const loadDaumPostcode = useCallback(() => {
    return new Promise((resolve) => {
      if (window.daum?.Postcode) { resolve(); return; }
      const s = document.createElement('script');
      s.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
      s.onload = () => resolve();
      document.head.appendChild(s);
    });
  }, []);

  const openAddressSearch = useCallback(async () => {
    setShowAddr(true);
    await loadDaumPostcode();
    setTimeout(() => {
      if (!addrRef.current || !window.daum?.Postcode) return;
      new window.daum.Postcode({
        oncomplete: (data) => {
          const addr = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
          setForm(prev => ({ ...prev, companyAddress: addr }));
          setShowAddr(false);
        },
        width: '100%',
        height: '100%',
      }).embed(addrRef.current);
    }, 100);
  }, [loadDaumPostcode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) { setError('이름을 입력해주세요.'); return; }
    if (!form.companyName.trim()) { setError('업체명을 입력해주세요.'); return; }
    if (!form.representative.trim()) { setError('대표자명을 입력해주세요.'); return; }
    if (!form.companyAddress.trim()) { setError('업체 주소를 입력해주세요.'); return; }

    setLoading(true);
    try {
      const uid = currentUser.uid;

      const updates = {
        name: form.name.trim(),
        companyName: form.companyName.trim(),
        representative: form.representative.trim(),
        companyAddress: form.companyAddress.trim(),
        companyAddressDetail: form.companyAddressDetail.trim(),
        status: 'pending',
        updatedAt: serverTimestamp(),
      };

      if (!userData?.phoneE164) {
        updates.phoneE164 = '';
        updates.phoneVerified = false;
      }

      await setDoc(doc(db, 'users', uid), updates, { merge: true });

      if (userData?.phoneE164) {
        await linkPhoneToUid({ uid, phoneE164: userData.phoneE164 });
      }

      await refreshUser();
      navigate('/login', {
        replace: true,
        state: { toast: '프로필이 저장되었습니다.\n관리자 승인 후 이용 가능합니다.' },
      });
    } catch (err) {
      console.error('프로필 저장 실패:', err);
      setError('프로필 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      {showAddr && (
        <AddrOverlay>
          <AddrHeader>
            <AddrHeaderTitle>주소 검색</AddrHeaderTitle>
            <AddrCloseBtn onClick={() => setShowAddr(false)}>닫기</AddrCloseBtn>
          </AddrHeader>
          <AddrEmbed ref={addrRef} />
        </AddrOverlay>
      )}
      <Card>
        <Title>프로필 완성</Title>
        <Desc>기본 정보를 입력해주세요.</Desc>

        <FormArea onSubmit={handleSubmit}>
          {!hasPrefilledName && (
            <>
              <Label>이름</Label>
              <Input
                name="name"
                placeholder="이름을 입력하세요"
                value={form.name}
                onChange={handleChange}
              />
            </>
          )}

          <Label>업체명</Label>
          <Input
            name="companyName"
            placeholder="업체명을 입력하세요"
            value={form.companyName}
            onChange={handleChange}
          />

          <Label>대표자명</Label>
          <Input
            name="representative"
            placeholder="대표자명을 입력하세요"
            value={form.representative}
            onChange={handleChange}
          />

          <Label>업체 주소</Label>
          <AddrRow>
            <AddrInput
              name="companyAddress"
              placeholder="주소 검색을 눌러주세요"
              value={form.companyAddress}
              readOnly
              onClick={openAddressSearch}
            />
            <AddrBtn type="button" onClick={openAddressSearch}>검색</AddrBtn>
          </AddrRow>
          <Input
            name="companyAddressDetail"
            placeholder="상세주소를 입력하세요"
            value={form.companyAddressDetail}
            onChange={handleChange}
            style={{ marginTop: 8 }}
          />

          {error && <ErrorText>{error}</ErrorText>}

          <SubmitBtn type="submit" disabled={loading}>
            {loading ? '저장 중...' : '프로필 저장'}
          </SubmitBtn>
        </FormArea>

        {!isIosWebView() && (
          <LogoutBtn onClick={() => { logout(); navigate('/login', { replace: true }); }}>
            로그인 화면으로 이동
          </LogoutBtn>
        )}
      </Card>
    </Container>
  );
};

export default CompleteProfilePage;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: linear-gradient(180deg, #1a1d29 0%, #F5F7FA 40%);
  padding: 24px;
  padding-top: 48px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: white;
  border-radius: 16px;
  padding: 32px 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
`;

const Title = styled.p`
  font-size: 20px;
  text-align: center;
  margin-bottom: 8px;
`;

const Desc = styled.p`
  font-size: 14px;
  color: #888;
  text-align: center;
  margin-bottom: 20px;
`;

const FormArea = styled.form``;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: #333;
  margin-bottom: 8px;
  margin-top: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  &:focus { border-color: ${ACCENT}; outline: none; }
`;

const ErrorText = styled.p`
  color: #EF5350;
  font-size: 13px;
  margin-top: 8px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 24px;
  border-radius: 24px;
  background: linear-gradient(135deg, #3B5534, #4A6741);
  color: white;
  font-size: 15px;
  &:disabled { opacity: 0.5; }
`;

const LogoutBtn = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 12px;
  background: none;
  color: #999;
  font-size: 14px;
  text-decoration: underline;
`;

const AddrOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #fff;
  display: flex;
  flex-direction: column;
`;

const AddrHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #E5E7EB;
`;

const AddrHeaderTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #1E2A32;
`;

const AddrCloseBtn = styled.button`
  font-size: 14px;
  color: #5A6B75;
`;

const AddrEmbed = styled.div`
  flex: 1;
`;

const AddrRow = styled.div`
  display: flex;
  gap: 8px;
`;

const AddrInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  background: #FAFAFA;
  cursor: pointer;
  &:focus { border-color: ${ACCENT}; outline: none; }
`;

const AddrBtn = styled.button`
  flex-shrink: 0;
  padding: 0 16px;
  border-radius: 8px;
  background: ${ACCENT};
  color: white;
  font-size: 14px;
  white-space: nowrap;
`;
